
import React from "react";
import { HyperText } from "@/components/ui/hyper-text";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface VibemailLogoProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  showLetterOnly?: boolean;
}

const VibemailLogo: React.FC<VibemailLogoProps> = ({ 
  className,
  size = "md",
  showLetterOnly = false
}) => {
  const navigate = useNavigate();
  
  // Size mapping for text size
  const sizeClasses = {
    sm: "text-xl",
    md: "text-2xl",
    lg: "text-4xl"
  };
  
  // Glow variants
  const glowVariants = {
    initial: {
      filter: "drop-shadow(0 0 2px rgba(24,176,141,0.3))"
    },
    animate: {
      filter: [
        "drop-shadow(0 0 2px rgba(24,176,141,0.3))",
        "drop-shadow(0 0 8px rgba(24,176,141,0.8))",
        "drop-shadow(0 0 2px rgba(24,176,141,0.3))"
      ],
      transition: { 
        duration: 2,
        repeat: Infinity, 
        repeatType: "mirror" as const
      }
    }
  };

  if (showLetterOnly) {
    return (
      <div 
        className={cn("cursor-pointer flex items-center justify-center", className)}
        onClick={() => navigate('/')}
      >
        <motion.div
          initial="initial"
          animate="animate"
          variants={glowVariants}
          className="w-8 h-8 rounded-full bg-jade/20 flex items-center justify-center"
        >
          <span className="font-bold text-jade">V</span>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div 
      className={cn("relative cursor-pointer flex items-center", className)}
      onClick={() => navigate('/')}
    >
      <motion.div
        initial="initial"
        animate="animate"
        variants={glowVariants}
        className="z-10"
      >
        <HyperText
          text="VIBEMAIL"
          className={cn(
            "font-bold text-jade", 
            sizeClasses[size]
          )}
          duration={1200}
          framerProps={{
            initial: { opacity: 0, y: -5 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: 3 },
          }}
        />
      </motion.div>
      
      {/* Background blur effect for enhanced glow */}
      <motion.div
        className="absolute inset-0 -z-10 bg-jade/10 rounded-full blur-xl"
        initial={{ opacity: 0.3 }}
        animate={{ 
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.05, 1]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "mirror"
        }}
      />
    </div>
  );
};

export default VibemailLogo;
